import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../api/dashboard';

interface KpiCardProps {
  label: string;
  value: string;
  sub: string;
  red?: boolean;
  onClick?: () => void;
}

function KpiCard({ label, value, sub, red, onClick }: KpiCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-white border text-left p-4 card-hover rounded-sm relative ${
        red ? 'border-red-200' : 'border-cream-200'
      }`}
    >
      {red && <div className="absolute top-0 right-0 w-1 h-full bg-red-500" />}
      <div className={`text-[10px] uppercase tracking-widest mb-1.5 ${red ? 'text-red-700/80' : 'text-navy-800/50'}`}>
        {label}
      </div>
      <div
        className={`serif-display text-2xl font-medium font-numeric ${red ? 'text-red-700' : 'text-navy-800'}`}
        dangerouslySetInnerHTML={{ __html: value }}
      />
      <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">{sub}</div>
    </button>
  );
}

function KpiSkeleton() {
  return (
    <div className="bg-white border border-cream-200 p-4 rounded-sm h-[105px]">
      <div className="h-3 w-20 bg-cream-200 rounded animate-pulse" />
      <div className="h-7 w-16 bg-cream-200 rounded animate-pulse mt-2" />
      <div className="h-3 w-24 bg-cream-200 rounded animate-pulse mt-3" />
    </div>
  );
}

export function KpiStrip() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'kpi-strip'],
    queryFn: dashboardApi.kpiStrip,
    refetchInterval: 60_000,
  });

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-sm text-red-700">
        Could not load KPIs. The API may be unreachable.
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)}
      </div>
    );
  }

  const fmtNum = (n: number) => n.toLocaleString('en-IN');
  return (
    <div className="grid grid-cols-6 gap-4">
      <KpiCard
        label="Beneficiaries"
        value={fmtNum(data.beneficiaries.value)}
        sub={data.beneficiaries.delta}
        onClick={() => navigate('/beneficiaries')}
      />
      <KpiCard
        label="Subsidy disbursed"
        value={`₹${data.subsidy_disbursed.value_cr.toFixed(1)}<span class="text-base text-navy-800/60 ml-1">Cr</span>`}
        sub={data.subsidy_disbursed.sub}
        onClick={() => navigate('/beneficiaries')}
      />
      <KpiCard
        label="Active applications"
        value={fmtNum(data.active_applications.value)}
        sub={data.active_applications.sub}
        onClick={() => navigate('/beneficiaries')}
      />
      <KpiCard
        label="SLA breaches"
        value={fmtNum(data.sla_breaches.value)}
        sub={data.sla_breaches.delta}
        red
      />
      <KpiCard
        label="Active MAITRI"
        value={fmtNum(data.active_maitri.value)}
        sub={data.active_maitri.sub}
        onClick={() => navigate('/maitri')}
      />
      <KpiCard
        label="Camps Q4"
        value={fmtNum(data.q4_camps.value)}
        sub={data.q4_camps.sub}
        onClick={() => navigate('/mela')}
      />
    </div>
  );
}
